/**
 * useAuthBootstrap — Auth/Profile state machine hook
 * ====================================================
 * Tek SSoT: Supabase session check + Backend `/profil` fetch + state transitions.
 *
 * Eski sistemde AuthContext + Layout + usePostLoginRedirect üçü ayrı ayrı
 * profile fetch + redirect kararı veriyordu, race condition kaçınılmazdı.
 * Bu hook her ikisini de tek state machine'e indirir.
 *
 * Kullanım:
 *   const auth = useAuthBootstrap();
 *   // auth.state, auth.profile, auth.error
 *   // auth.logout(), auth.refetch()
 *   // Sadece <AuthGate> component'i state'i tüketir + redirect karar verir.
 *
 * Race-safety:
 *   - Tek state machine (boolean kombinasyonu yok)
 *   - "Henüz fetch edilmedi" (BOOTING/LOADING_PROFILE) state'leri explicit
 *   - Backoff retry sırasında state hep TRANSIENT_ERR — redirect tetiklenmez
 *   - Stale request guard (abort controller)
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/shared/utils/supabaseClient';
import axiosInstance, { markLogoutInProgress } from '@/shared/utils/axiosConfig';
import {
  type AuthSnapshot,
  type AuthError,
  type AuthProfile,
  INITIAL_SNAPSHOT,
  classifyError,
  backoffMs,
  MAX_RETRIES,
  snapshotWith
} from './authState';
import { emitAuthTelemetry } from './authTelemetry';

// Auth debug log'ları kapalı default. Açmak için:
//   localStorage.setItem('riskmatik.auth.debug', '1') + sayfayı yenile
const DEBUG = typeof window !== 'undefined' &&
  window.localStorage?.getItem('riskmatik.auth.debug') === '1';

// Telemetry'ye SADECE bu event'ler gider (volume azaltma). Diğerleri
// sadece dev console log'una düşer. SSoT: authTelemetry.ts → backend whitelist
// ile senkron tutulur.
const TELEMETRY_EVENTS = new Set<string>([
  'state.transition.terminal',
  'fetchProfile.error',
  'fetchProfile.retry.cancelled',
  'authgate.redirect'
]);

// Terminal state'ler (telemetry event üretimi için) — module scope, per-render leak yok
const TERMINAL_STATES = new Set<string>([
  'READY', 'NEEDS_PROFILE', 'ACCOUNT_SUSPENDED', 'NO_SESSION'
]);

/**
 * Firma listesi response unwrap helper.
 * Backend `res.success({ firmalar: [...] })` → envelope: { success, data, _meta }
 * veya doğrudan { firmalar: [...] }. Her ikisini de destekle (drift defense).
 */
function unwrapFirmalar(payload: unknown): Array<{ id: string }> {
  const root = (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>))
    ? (payload as { data: unknown }).data
    : payload;
  const firmalar = (root as { firmalar?: unknown })?.firmalar;
  return Array.isArray(firmalar) ? (firmalar as Array<{ id: string }>) : [];
}

function debugLog(action: string, payload?: Record<string, unknown>): void {
  if (TELEMETRY_EVENTS.has(action)) {
    emitAuthTelemetry(action, payload);
  }
  if (!DEBUG) return;
  // eslint-disable-next-line no-console
  console.log(`[auth] ${action}`, payload ?? '');
}

export interface UseAuthBootstrapApi extends AuthSnapshot {
  /** Manuel profil yenileme (örn. profil güncelleme sonrası) */
  refetch: () => Promise<void>;
  /** Logout: tüm state temizle + Supabase signout + landing'e redirect */
  logout: () => Promise<void>;
  /** Profil state'i local olarak güncelle (örn. updateProfil sonrası) */
  setProfileLocal: (profile: AuthProfile) => void;
}

export function useAuthBootstrap(): UseAuthBootstrapApi {
  const [snapshot, setSnapshot] = useState<AuthSnapshot>(INITIAL_SNAPSHOT);
  const snapshotRef = useRef<AuthSnapshot>(INITIAL_SNAPSHOT);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  // Snapshot ref guncel kalsın (closure'da kullanılıyor)
  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  const updateSnapshot = useCallback((patch: Partial<AuthSnapshot>) => {
    if (!mountedRef.current) return;
    setSnapshot((prev) => {
      const next = snapshotWith(prev, patch);
      const isTerminal = TERMINAL_STATES.has(next.state) && prev.state !== next.state;
      const eventName = isTerminal ? 'state.transition.terminal' : 'state.transition';
      debugLog(eventName, { from: prev.state, to: next.state });
      return next;
    });
  }, []);

  /**
   * Profile fetch — exponential backoff + retry. State sadece terminal'de set edilir.
   *
   * Race-safety invariants (4-ajan review P0 fix'leri):
   *   1. AbortController.signal'i tüm await noktalarında check edilir
   *   2. Retry sleep abortable (controller.signal.addEventListener('abort'))
   *   3. userId değişimi (multi-account hijack) retry öncesinde guard edilir
   *   4. Stale closure'lardan kaçınmak için snapshotRef üzerinden okuma
   */
  const fetchProfile = useCallback(
    async (userId: string, email: string, retry = 0): Promise<void> => {
      // Önceki request'i abort et (stale response leak'i önle)
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // İlk denemede LOADING_PROFILE'a geç (transient retry sırasında state korunur)
      if (retry === 0) {
        updateSnapshot({ state: 'LOADING_PROFILE', userId, email, retryCount: 0 });
      }

      try {
        const timestamp = Date.now();
        const { data } = await axiosInstance.get(`/profil?_t=${timestamp}`, {
          signal: controller.signal
        });

        if (controller.signal.aborted || !mountedRef.current) return;

        // Backend res.success({...profil, _meta}) → envelope: {success:true, data}
        // veya çıplak object dönüyor. Her ikisini de destekle.
        const profile = (data?.data ?? data) as AuthProfile;

        if (!profile || !profile.id) {
          // Backend 200 döndü ama beklenmedik shape — transient say
          throw new Error('Invalid profile response shape');
        }

        // Account status kontrolü (kalıcı state).
        // Schema'da `aktifMi` boolean SSoT — false = suspended/soft-deleted.
        const isInactive = profile.aktifMi === false;

        if (isInactive) {
          updateSnapshot({
            state: 'ACCOUNT_SUSPENDED',
            profile,
            error: {
              code: 'ACCOUNT_SUSPENDED',
              message: 'Hesabınız aktif değil.',
              isTransient: false
            },
            retryCount: 0
          });
          return;
        }

        // Profil OK → LOADING_FIRMA aşamasına geç ve firma sayısını yükle.
        // Bu state machine'in READY'ye girmesinden ÖNCE yapılır çünkü AuthGate
        // post-login redirect kararını firmaCount'a göre veriyor (resolver).
        // Firma fetch başarısızlığında READY'ye düşer + firmaCount=0 güvenli default
        // → kullanıcı onboarding'e yönlendirilir (kilitlenmeyiz, sadece "0 firma" sayar).
        updateSnapshot({ state: 'LOADING_FIRMA', profile, error: null, retryCount: 0 });

        let firmaCount = 0;
        let singleFirmaId: string | null = null;
        try {
          const firmaRes = await axiosInstance.get('/firmalar', {
            signal: controller.signal
          });
          if (controller.signal.aborted || !mountedRef.current) return;
          const list = unwrapFirmalar(firmaRes.data);
          firmaCount = list.length;
          if (firmaCount === 1) singleFirmaId = list[0].id;
        } catch (firmaErr) {
          if (controller.signal.aborted || !mountedRef.current) return;
          // Firma fetch fail-soft: state machine'in READY'ye geçmesini engelleme.
          // firmaCount=0 default → resolver onboarding'e atar (en güvenli rota).
          debugLog('fetchProfile.firma.error', {
            error: (firmaErr as Error)?.message,
          });
        }

        // READY — profil + firma context hazır
        updateSnapshot({
          state: 'READY',
          firmaCount,
          singleFirmaId,
          error: null,
          retryCount: 0
        });
      } catch (err) {
        if (controller.signal.aborted || !mountedRef.current) return;

        const authErr: AuthError = classifyError(err);
        debugLog('fetchProfile.error', { retry, error_code: authErr.code, status: authErr.status });

        // 401 → logout (Supabase token invalid)
        if (authErr.code === 'UNAUTHENTICATED') {
          await supabase.auth.signOut().catch(() => {});
          updateSnapshot({ state: 'NO_SESSION', userId: null, email: null, profile: null, error: null });
          return;
        }

        // PROFILE_NOT_FOUND → kalıcı state, retry yok
        if (authErr.code === 'PROFILE_NOT_FOUND') {
          updateSnapshot({ state: 'NEEDS_PROFILE', error: authErr, retryCount: 0 });
          return;
        }

        // Transient hata: backoff retry
        if (authErr.isTransient && retry < MAX_RETRIES) {
          // KVKK m.12: TRANSIENT_ERR'de profile snapshot'ı stale işaretle. UI
          // protected sayfada eski cached profile ile devam edebilir AMA Layer
          // tüketicileri staleSince'a bakıp read-only/banner modu açar.
          updateSnapshot({
            state: 'TRANSIENT_ERR',
            error: authErr,
            retryCount: retry + 1
          });
          const wait = backoffMs(retry);
          debugLog('fetchProfile.retry', { retry: retry + 1, waitMs: wait });

          // Abortable sleep — controller.signal abort ederse hemen çık.
          // Bu sayede yeni session/logout sırasında zombie retry zinciri yok.
          try {
            await new Promise<void>((resolve, reject) => {
              const t = setTimeout(resolve, wait);
              controller.signal.addEventListener('abort', () => {
                clearTimeout(t);
                reject(new Error('aborted'));
              }, { once: true });
            });
          } catch {
            return; // abort edildi
          }

          if (!mountedRef.current || controller.signal.aborted) return;

          // Multi-account hijack guard: retry sleep sırasında kullanıcı değişmiş
          // olabilir (logout + yeni login). snapshot.userId değiştiyse retry'ı
          // iptal et — yeni kullanıcının session'ına eski user'ın profilini yazma.
          if (snapshotRef.current.userId !== userId) {
            debugLog('fetchProfile.retry.cancelled', {
              reason: 'userId_changed',
              expected: userId,
              actual: snapshotRef.current.userId
            });
            return;
          }

          return fetchProfile(userId, email, retry + 1);
        }

        // Retry tükendi veya non-transient — TRANSIENT_ERR'de bırak
        // (kullanıcı yerinden çıkmasın, manuel refetch veya focus revalidate ile devam)
        updateSnapshot({ state: 'TRANSIENT_ERR', error: authErr, retryCount: retry });
      }
    },
    [updateSnapshot]
  );

  /** Session check — Supabase'den oturum al, varsa profile fetch tetikle */
  const checkSession = useCallback(async (): Promise<void> => {
    updateSnapshot({ state: 'CHECKING_SESSION' });
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error || !data?.session) {
        debugLog('checkSession', { result: 'no_session', error: error?.message });
        updateSnapshot({ state: 'NO_SESSION', userId: null, email: null, profile: null, error: null });
        return;
      }

      const { user } = data.session;
      await fetchProfile(user.id, user.email || '');
    } catch (err) {
      debugLog('checkSession.error', { error: (err as Error)?.message });
      updateSnapshot({
        state: 'NO_SESSION',
        userId: null,
        email: null,
        profile: null,
        error: null
      });
    }
  }, [fetchProfile, updateSnapshot]);

  // Public API methods

  const refetch = useCallback(async () => {
    const { userId, email } = snapshotRef.current;
    if (!userId) {
      // Oturum yoksa session check'le başla
      await checkSession();
      return;
    }
    await fetchProfile(userId, email || '');
  }, [checkSession, fetchProfile]);

  const logout = useCallback(async () => {
    debugLog('logout');
    // Bilinçli çıkış → interceptor'ın 401 "oturum süresi doldu" yanlış-pozitifini bastır
    // (in-flight SWR poll token silinince 401 döner; landing yönlendirmesini ezmesin).
    markLogoutInProgress();
    abortRef.current?.abort();

    // 1) localStorage temizliği — sync, instant
    if (typeof window !== 'undefined') {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('sb-')) localStorage.removeItem(key);
      });
      localStorage.removeItem('riskmatik.activeFirmaId');
      localStorage.removeItem('riskmatik.recentFirmaIds');
    }

    // 2) State machine'i hemen NO_SESSION'a al — UI freeze yok
    updateSnapshot({
      state: 'NO_SESSION',
      userId: null,
      email: null,
      profile: null,
      firmaCount: 0,
      singleFirmaId: null,
      error: null,
      retryCount: 0
    });

    // 3) Supabase signOut fire-and-forget — local scope, sadece SDK internal state
    //    temizliği. Await edilirse 500-1500ms UI'yi donduruyordu ("kağnı gibi").
    //    Token zaten localStorage'dan silindi, signOut() backend'e gitmez (scope: local).
    supabase.auth.signOut({ scope: 'local' }).catch(() => {});
  }, [updateSnapshot]);

  const setProfileLocal = useCallback(
    (profile: AuthProfile) => {
      updateSnapshot({ profile, state: 'READY', error: null });
    },
    [updateSnapshot]
  );

  // Initial mount: session check + Supabase auth state listener
  useEffect(() => {
    mountedRef.current = true;
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      debugLog('supabase.event', { event, hasSession: !!session });

      // INITIAL_SESSION — Supabase v2 mount'ta bu event'i fire eder. checkSession()
      // zaten paralel çalıştırıldı → double-fetch race'i önlemek için no-op.
      if (event === 'INITIAL_SESSION') return;

      if (event === 'SIGNED_IN' && session?.user) {
        // Aynı kullanıcı + zaten READY/LOADING_PROFILE → re-fetch etme (visibility
        // refresh, token refresh sonrası SIGNED_IN tekrarı vb.)
        const sameUser = snapshotRef.current.userId === session.user.id;
        const stableState =
          snapshotRef.current.state === 'READY' ||
          snapshotRef.current.state === 'LOADING_PROFILE' ||
          snapshotRef.current.state === 'LOADING_FIRMA';
        if (sameUser && stableState) return;

        fetchProfile(session.user.id, session.user.email || '');
      } else if (event === 'SIGNED_OUT') {
        updateSnapshot({
          state: 'NO_SESSION',
          userId: null,
          email: null,
          profile: null,
          error: null
        });
      }
      // TOKEN_REFRESHED → no-op (session devam ediyor, axios interceptor yeni token alır)
    });

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus / online → TRANSIENT_ERR varsa otomatik retry (SWR-vari)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = () => {
      const s = snapshotRef.current;
      if (s.state === 'TRANSIENT_ERR' && s.userId) {
        debugLog('focus.retry');
        fetchProfile(s.userId, s.email || '');
      }
    };

    window.addEventListener('focus', handler);
    window.addEventListener('online', handler);
    return () => {
      window.removeEventListener('focus', handler);
      window.removeEventListener('online', handler);
    };
  }, [fetchProfile]);

  return {
    ...snapshot,
    refetch,
    logout,
    setProfileLocal
  };
}
